import type { JourneyPage } from './types'

export const meetingToDone: JourneyPage = {
  slug: 'meeting-to-done',
  intro: [
    { text: 'You have a client meeting at ', highlight: '10am' },
    { text: 'By ', highlight: 'lunch, the report is done' },
    { text: 'Transcribed. Researched. ', highlight: 'Written' },
    { text: 'You just reviewed and ', highlight: 'clicked send' },
    { text: 'This is ', highlight: 'WealthSpace' },
  ],
  colorClass: 'meeting',
  seo: {
    title: 'WealthSpace Meeting to Done | AI Suitability Reports',
    description:
      'Transform client meetings into compliant suitability reports automatically. AI transcription, research, and report writing for UK financial advisers.',
    keywords:
      'meeting notes, AI transcription, suitability reports, financial adviser meetings, client meeting software, adviser AI, FCA compliance, meeting recording',
  },
  hero: {
    eyebrow: 'Imagine your next client meeting',
    title: 'From conversation to',
    titleAccent: 'compliant report',
    subtitle:
      'Watch what happens when you have a client meeting with WealthSpace. The conversation flows naturally. Everything else happens automatically.',
    microcopy: 'No typing. No templates. No late nights.',
  },
  journeySection: {
    label: 'The Story',
    title: 'One meeting.',
    titleAccent: 'Everything done.',
    subtitle:
      'This is what happens when you meet with Mr & Mrs Thompson about their retirement planning.',
  },
  timeline: [
    {
      time: '10:00 AM',
      icon: 'video',
      iconColor: 'violet',
      title: 'The meeting begins',
      meta: 'Video call with Mr & Mrs Thompson',
      body: "You click \"Record\" and focus entirely on the Thompsons. They're worried about whether they can retire at 60. You explore their pensions, their rental property, and that ISA they forgot about. It's a real conversation - not a form-filling exercise.",
      previewLabel: 'Meanwhile, WealthSpace is...',
      previewText:
        'Recording audio and video, identifying speakers, and beginning real-time transcription.',
    },
    {
      time: '11:15 AM - Meeting ends',
      icon: 'document',
      iconColor: 'teal',
      title: 'Instant transcription',
      meta: 'Complete in 2 minutes',
      body: "The meeting ends. You say goodbye. By the time you've made a cup of tea, the full transcription is ready - with speakers identified and key moments timestamped.",
      previewLabel: 'From the transcript',
      previewText:
        'Mrs Thompson [34:12]: "We\'d really like to help the kids with house deposits, but not if it means we can\'t retire when we planned."',
    },
    {
      time: '11:20 AM',
      icon: 'clipboard',
      iconColor: 'amber',
      title: 'Structured file note',
      meta: 'AI-generated, ready to review',
      body: 'WealthSpace extracts the key facts, objectives, and agreed next steps. A proper file note - the kind you\'d write if you had unlimited time - appears in your dashboard.',
      previewLabel: 'Extracted automatically',
      previewText:
        'Client objectives: Retire at 60, maintain current lifestyle, gift £50k to each child for house deposits, protect against care costs.',
    },
    {
      time: '11:25 AM',
      icon: 'search',
      iconColor: 'emerald',
      title: 'Product research',
      meta: 'Automatic comparison running',
      body: "Based on the Thompsons' situation, WealthSpace researches suitable pension drawdown options, compares providers, and pulls current charges and features. All sourced and cited.",
      previewLabel: 'Research findings',
      previewText:
        '3 providers identified for flexi-access drawdown. Detailed comparison including AMC, platform charges, fund range, and drawdown flexibility prepared.',
    },
    {
      time: '11:35 AM',
      icon: 'sparkle',
      iconColor: 'rose',
      title: 'Draft suitability report',
      meta: 'Ready for your review',
      body: "A complete suitability report draft appears. It knows what you discussed, references the research, and is written in your firm's tone. Compliance flags are already checked. You just need to review and approve.",
      previewLabel: 'Report excerpt',
      previewText:
        '"Based on our discussion, I recommend consolidating your existing pension arrangements into a flexi-access drawdown plan with [Provider]. This recommendation takes into account your desire to retire at 60 while maintaining the flexibility to..."',
    },
  ],
  result: {
    title: 'Meeting at 10. Report ready by',
    titleAccent: 'lunch.',
    body: "You spent 75 minutes with the Thompsons. Everything else - the transcription, the notes, the research, the report draft - happened while you weren't looking.",
    stats: [
      { value: '35 min', label: 'Total time to review & send' },
      { value: '3+ hrs', label: 'Saved vs. manual process' },
      { value: '100%', label: 'Compliance checks passed' },
    ],
  },
  features: {
    label: 'Under the hood',
    title: "What's actually",
    titleAccent: 'happening',
    subtitle: "While you're focused on your client, WealthSpace is doing the heavy lifting.",
    items: [
      {
        icon: 'mic',
        title: 'Intelligent listening',
        body: 'Real-time transcription that identifies speakers, catches financial terminology correctly, and timestamps important moments for easy reference later.',
      },
      {
        icon: 'sparkle',
        title: 'Context-aware AI',
        body: 'The AI understands UK financial advice. It knows what a SIPP is, how pension transfers work, and what the FCA expects in a suitability report.',
      },
      {
        icon: 'shield',
        title: 'Built-in compliance',
        body: 'Every report is checked against FCA COBS rules before you even see it. Gaps are flagged, disclosures are included, and audit trails are automatic.',
      },
    ],
  },
  testimonial: {
    quote:
      'We built Meeting to Done because we lived it. The afternoon of admin after every client meeting. The backlog of reports. The guilt. We knew there had to be a better way - and now there is.',
    authorName: 'The WealthSpace Team',
    authorRole: 'Built by Advisers, for Advisers',
    initials: 'WS',
  },
  cta: {
    title: 'See it happen with',
    titleAccent: 'your',
    subtitle:
      "Book a demo and we'll show you exactly how a real client meeting turns into a finished report.",
    note: 'Free. Friendly. Start with your very next client meeting.',
  },
}
